import {
  ContractHistoryActivityPeriodFragment,
  ContractHistoryContractPeriodFragment,
  ContractHistoryContractVersionChangesFragment,
  ContractHistoryContractVersionHistoryFragment,
  ContractsSpanishAgreementCodeDocument,
  ContractsSpanishClassificationDocument,
  ContractsSpanishCnoOccupationDocument,
  ContractsSpanishContributionTypeDocument,
  ContractsSpanishOccupationCodeDocument,
  ContractsSpanishRegimeDocument,
  JobCatalogTreeLevelNodeFragment,
  PayrollConceptsForBaseSalaryTypeDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import centsToDecimal from 'modules/core/lib/formatCents'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import { getContractVersionJobCatalogTreeLevelNode } from 'modules/contracts/components/ContractHistory/lib/getJobCatalogTreeLevelNode'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import { Contract, ContractDetail } from 'modules/contracts/components/NewContractHistory/types'
import getFieldKey from 'modules/contracts/lib/getFieldKey'

import { calculateDateDifference } from './contractDates'
import ContractVersionContract from './contractVersionContract'

const BOOLEAN_KEYS = ['has_payroll', 'has_trial_period', 'es_has_teleworking_contract']
const SALARY_KEYS = ['salary_amount', 'salary_frequency']
interface GetContractTitleParams {
  actionType: string
  firstContractVersion: boolean
  firstContract: boolean
  isSingleUpcomingActivityPeriod?: boolean
}

const getCurrentContract = (contracts: Contract[] | ContractDetail[]) => {
  return contracts.find((c) => c.schedule === 'ongoing')
}

export const findGlobalOngoingActivityPeriod = (
  contracts: Contract[] | ContractDetail[]
): ContractHistoryActivityPeriodFragment | undefined => {
  const current = getCurrentContract(contracts) || contracts[contracts.length - 1]
  if (!current) return undefined

  const contractContract = ContractVersionContract.fromContract(current)
  return contractContract.getLatestOngoingPeriod() || contractContract.activityPeriods[0]
}

export const getContractVersionTitle = ({
  actionType,
  firstContractVersion,
  firstContract,
  isSingleUpcomingActivityPeriod,
}: GetContractTitleParams) => {
  if (isSingleUpcomingActivityPeriod) {
    return i18n.t('contract_versions.navigator.timeline.updated_activity_and_conditions')
  }

  if (firstContractVersion) {
    return firstContract
      ? i18n.t('contract_versions.navigator.timeline.hired')
      : i18n.t('contract_versions.navigator.timeline.rehired')
  }

  if (actionType === FLOWS.promotion) {
    return i18n.t('contract_versions.navigator.timeline.promotion')
  }

  return i18n.t('contract_versions.navigator.timeline.updated_contract')
}

export const getContractTitle = ({ contract }: { contract: Contract }) => {
  if (contract.schedule === 'ongoing') {
    return {
      title: i18n.t('contract_versions.navigator.history.contract_card.title.current_contract'),
      subtitle: moment(contract.startsOn).format('MMM Do YYYY'),
    }
  }

  if (contract.schedule === 'upcoming') {
    return {
      title: i18n.t('contract_versions.navigator.history.contract_card.title.upcoming_contract'),
      subtitle: undefined,
    }
  }

  const endDate = contract.endsOn
  const subtitle = endDate ? moment(endDate).format('MMM Do YYYY') : undefined

  return {
    title: i18n.t('contract_versions.navigator.history.contract_card.title.ended_contract'),
    subtitle,
  }
}

export const getVersionData = (
  referenceVersionId: number,
  versions: ContractHistoryContractVersionHistoryFragment[]
) => {
  return versions.find((v) => v.id === referenceVersionId)
}

export const getPeriodTime = (period: ContractHistoryContractPeriodFragment) => {
  if (!period.from) return undefined

  const start = moment(period.from)

  if (start.isAfter(moment())) return undefined

  const end = period.to ? moment(period.to) : moment()
  const { years, months, days } = calculateDateDifference(start, end)
  const daysWithToday = days + 1
  const parts: string[] = []

  if (years > 0) {
    parts.push(i18n.t('contract_versions.navigator.history.time.years', { years }))
  }

  if (months > 0) {
    parts.push(i18n.t('contract_versions.navigator.history.time.months', { months }))
  }

  if (daysWithToday > 0) {
    parts.push(
      i18n.tp('contract_versions.navigator.history.time.days_count', { count: daysWithToday })
    )
  }

  if (parts.length === 0) {
    return null
  }

  return parts.join(', ')
}

export const getPeriodTimeWithSince = (period: ContractHistoryContractPeriodFragment) => {
  if (!period.from) return undefined

  if (!period.to) {
    return moment(period.from).format('MMM Do YYYY')
  }

  return `${moment(period.from).format('MMM Do YYYY')} - ${moment(period.to).format('MMM Do YYYY')}`
}

const getSalaryFrequency = (salaryFrequency: string | undefined) => {
  switch (salaryFrequency) {
    case 'yearly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.yearly')
    case 'monthly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.monthly')
    case 'weekly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.weekly')
    case 'daily':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.daily')
    case 'hourly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.hourly')
  }
}

const formatSalary = (
  frequency?: string,
  amount?: number,
  legalEntityId?: number
): string | null => {
  if (!amount) return null

  const currency = formatCurrency(amount, undefined, null, legalEntityId)
  if (!currency) return null

  return frequency ? `${currency} ${getSalaryFrequency(frequency)}` : currency
}

const formatSalaryUpdate = (
  updates: ContractHistoryContractVersionChangesFragment['updates'],
  legalEntityId: number
) => {
  const salaryAmount = updates.find((u) => u.field === 'salary_amount')
  const salaryFrequency = updates.find((u) => u.field === 'salary_frequency')

  return {
    field: getFieldKey('gross_salary'),
    newValue:
      formatSalary(
        salaryFrequency?.newValue || undefined,
        salaryAmount?.newValue ? Number(salaryAmount.newValue) : undefined,
        legalEntityId
      ) || '',
    oldValue:
      formatSalary(
        salaryFrequency?.oldValue || undefined,
        salaryAmount?.oldValue ? Number(salaryAmount.oldValue) : undefined,
        legalEntityId
      ) || undefined,
  }
}

const formatHours = (
  update: ContractHistoryContractVersionChangesFragment['updates'][number],
  field: string
) => ({
  field: getFieldKey(field),
  newValue: update.newValue ? centsToDecimal(Number(update.newValue)) : '',
  oldValue: update.oldValue ? centsToDecimal(Number(update.oldValue)) : undefined,
})

const formatBankHolidayTreatmentValue = (bankHolidayTreatment: string) => {
  switch (bankHolidayTreatment) {
    case 'workable':
      return i18n.t('contract_versions.time.bank_holidays.working_time')
    case 'non_workable':
      return i18n.t('contract_versions.time.bank_holidays.non_working_time')
  }
}

const formatAnnualWorkingTimeDistribution = (annualWorkingTimeDistribution: string) => {
  switch (annualWorkingTimeDistribution) {
    case 'limit_workdays':
      return i18n.t('contract_versions.time.annual_working_time_distribution.limit_workdays')
    case 'limit_daily_hours':
      return i18n.t('contract_versions.time.annual_working_time_distribution.limit_daily_hours')
  }
}

const formatWorkingWeekDays = (workingWeekDays: string | null | undefined): string => {
  if (!workingWeekDays) return ''

  return workingWeekDays
    .split(',')
    .map((day) => day.trim().charAt(0).toUpperCase() + day.trim().slice(1).toLowerCase())
    .join(', ')
}

const formatBooleanKey = (
  update: ContractHistoryContractVersionChangesFragment['updates'][number]
) => {
  const formatBooleanValue = (value: string | null | undefined): string => {
    if (!value) return ''

    return value === 'true'
      ? i18n.t('contract_versions.navigator.changes.yes')
      : i18n.t('contract_versions.navigator.changes.no')
  }

  return {
    field: getFieldKey(update.field),
    newValue: formatBooleanValue(update.newValue),
    oldValue: formatBooleanValue(update.oldValue),
  }
}

const FormatA3innuvaClassificationCode = (id: number) => {
  const { data: classificationsData } = useQuery(ContractsSpanishClassificationDocument)

  const a3innuvaClassifications = classificationsData?.contracts?.spanishClassifications ?? []
  return a3innuvaClassifications.find((classification) => classification.id === id)?.name
}

const FormatA3innuvaAgreementCode = (id: number) => {
  const { data: agreementCodesData } = useQuery(ContractsSpanishAgreementCodeDocument)

  const agreementCodes = agreementCodesData?.contracts?.spanishAgreementCodes ?? []
  return agreementCodes.find((agreementCode) => agreementCode.id === id)?.name
}

const FormatA3innuvaRegime = (id: number) => {
  const { data: regimesData } = useQuery(ContractsSpanishRegimeDocument)

  const regimes = regimesData?.contracts?.spanishRegimes ?? []
  return regimes.find((regime) => regime.id === id)?.name
}

const FormatA3innuvaOccupationCode = (id: number) => {
  const { data: occupationCodesData } = useQuery(ContractsSpanishOccupationCodeDocument)

  const occupationCodes = occupationCodesData?.contracts?.spanishOccupationCodes ?? []
  return occupationCodes.find((occupationCode) => occupationCode.id === id)?.name
}

const FormatA3innuvaCnoOccupation = (id: number) => {
  const { data: cnoOccupationsData } = useQuery(ContractsSpanishCnoOccupationDocument)

  const cnoOccupations = cnoOccupationsData?.contracts?.spanishCnoOccupations ?? []
  return cnoOccupations.find((cnoOccupation) => cnoOccupation.id === id)?.name
}

const FormatA3innuvaContributionType = (id: number) => {
  const { data: contributionTypesData } = useQuery(ContractsSpanishContributionTypeDocument)

  const contributionTypes = contributionTypesData?.contracts?.spanishContributionTypes ?? []
  return contributionTypes.find((contributionType) => contributionType.id === id)?.name
}

const FormatDeBaseSalaryType = (id: number) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data: baseSalaryTypesData } = useQuery(PayrollConceptsForBaseSalaryTypeDocument, {
    variables: {
      companyId: companyId,
    },
  })

  const baseSalaryTypes = baseSalaryTypesData?.payroll.conceptsConnection.nodes ?? []
  return baseSalaryTypes.find((baseSalaryType) => baseSalaryType.id === id)?.label
}

export function mapUpdate(
  update: ContractHistoryContractVersionChangesFragment['updates'][number],
  updates: ContractHistoryContractVersionChangesFragment['updates'],
  legalEntityId: number,
  jobCatalogTreeLevelNodes?: JobCatalogTreeLevelNodeFragment[]
) {
  if (BOOLEAN_KEYS.includes(update.field)) {
    return formatBooleanKey(update)
  }

  if (SALARY_KEYS.includes(update.field)) {
    return update.field === 'salary_frequency' ? null : formatSalaryUpdate(updates, legalEntityId)
  }

  if (
    update.field === 'maximum_weekly_hours' ||
    update.field === 'max_legal_yearly_hours' ||
    update.field === 'max_work_minutes_per_day' ||
    update.field === 'min_rest_minutes_between_days'
  ) {
    return formatHours(update, update.field)
  }

  if (update.field === 'bank_holiday_treatment') {
    return {
      field: getFieldKey('bank_holiday_treatment'),
      newValue: update.newValue ? formatBankHolidayTreatmentValue(update.newValue) : '',
      oldValue: update.oldValue ? formatBankHolidayTreatmentValue(update.oldValue) : '',
    }
  }

  if (update.field === 'annual_working_time_distribution') {
    return {
      field: getFieldKey('annual_working_time_distribution'),
      newValue: update.newValue ? formatAnnualWorkingTimeDistribution(update.newValue) : '',
      oldValue: update.oldValue ? formatAnnualWorkingTimeDistribution(update.oldValue) : '',
    }
  }

  if (update.field === 'working_week_days') {
    return {
      field: getFieldKey('working_week_days'),
      newValue: formatWorkingWeekDays(update.newValue),
      oldValue: formatWorkingWeekDays(update.oldValue),
    }
  }

  if (update.field === 'job_catalog_tree_node_uuid') {
    const currentJobCatalogTreeLevelNode = getContractVersionJobCatalogTreeLevelNode(
      jobCatalogTreeLevelNodes,
      update.newValue
    )

    const previousJobCatalogTreeLevelNode = getContractVersionJobCatalogTreeLevelNode(
      jobCatalogTreeLevelNodes,
      update.oldValue
    )

    return {
      field: getFieldKey('job_catalog_tree_node_uuid'),
      newValue: currentJobCatalogTreeLevelNode?.name,
      oldValue: previousJobCatalogTreeLevelNode?.name,
    }
  }

  if (update.field === 'working_time_percentage_in_cents') {
    return {
      field: getFieldKey('working_time_percentage_in_cents'),
      newValue: update.newValue ? centsToDecimal(Number(update.newValue)) : '',
      oldValue: update.oldValue ? centsToDecimal(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_classification_id') {
    return {
      field: getFieldKey('contracts_es_classification_id'),
      newValue: update.newValue ? FormatA3innuvaClassificationCode(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaClassificationCode(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_agreement_code_id') {
    return {
      field: getFieldKey('contracts_es_agreement_code_id'),
      newValue: update.newValue ? FormatA3innuvaAgreementCode(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaAgreementCode(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_regime_id') {
    return {
      field: getFieldKey('contracts_es_regime_id'),
      newValue: update.newValue ? FormatA3innuvaRegime(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaRegime(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_occupation_code_id') {
    return {
      field: getFieldKey('contracts_es_occupation_code_id'),
      newValue: update.newValue ? FormatA3innuvaOccupationCode(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaOccupationCode(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_cno_occupation_id') {
    return {
      field: getFieldKey('contracts_es_cno_occupation_id'),
      newValue: update.newValue ? FormatA3innuvaCnoOccupation(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaCnoOccupation(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'contracts_es_contribution_type_id') {
    return {
      field: getFieldKey('contracts_es_contribution_type_id'),
      newValue: update.newValue ? FormatA3innuvaContributionType(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatA3innuvaContributionType(Number(update.oldValue)) : '',
    }
  }

  if (update.field === 'de_base_salary_type_id') {
    return {
      field: getFieldKey('de_base_salary_type_id'),
      newValue: update.newValue ? FormatDeBaseSalaryType(Number(update.newValue)) : '',
      oldValue: update.oldValue ? FormatDeBaseSalaryType(Number(update.oldValue)) : '',
    }
  }

  return {
    field: getFieldKey(update.field),
    newValue: update.newValue,
    oldValue: update.oldValue,
  }
}
